import useNotification from '@/composables/notifications'
import useAuth from '@/modules/auth/composables/use-auth'
import { type Axios, AxiosError, type AxiosInstance, type AxiosResponse } from 'axios'
import { ENDPOINTS } from '@/modules/auth/api.ts'
import { builderAxiosInstance } from '@/api/builder/useBuilderApiClient'

let refreshTokenPromise: null | Promise<void> = null

const tokenErrorStatuses = [401, 403] // backend responds with 401 for invalid token but 403 for no token

export const handleExpiredSession = async (parentError: ANY, axiosInstance: Axios) => {
    const { refreshTokenAction, logOut } = useAuth()

    if (parentError.config._retry) {
        return Promise.reject(parentError)
    }
    if (!refreshTokenPromise) {
        refreshTokenPromise = refreshTokenAction()
    }

    try {
        await refreshTokenPromise
        const repeatedConfig = { ...parentError.config }
        return axiosInstance.request({ ...repeatedConfig, _retry: true })
    } catch (error: unknown) {
        const status = ((error as AxiosError | undefined)?.response?.status) || 0
        const forceLogOut = parentError.config.url !== ENDPOINTS.USERS_ME || tokenErrorStatuses.includes(status)

        if (forceLogOut) {
            const response = await logOut()

            if (response?.data.redirectUrl) {
                window.location.href = response.data.redirectUrl
            }
        }
        return Promise.reject(error || parentError)
    } finally {
        refreshTokenPromise = null
    }
}

export async function misleadingHtmlResponseInterceptor(res: AxiosResponse) {
    const expectedJson = (res.config.headers.get('accept') as string | undefined)?.includes('application/json')
    const receivedHtml = (res.headers['content-type'] as string | undefined)?.includes('text/html')

    // due to config issues, CloudFront can swallow proper 40x error and replace it with 200 with HTML
    if (expectedJson && receivedHtml) {
        return await handleExpiredSession(res, builderAxiosInstance)
    }

    return res
}

export async function expiredSessionInterceptor(error: ANY, axiosInstance: AxiosInstance) {
    if (
        tokenErrorStatuses.includes(error?.response?.status)
        && error.config.url !== '/refresh'
        && !error.config._retry
    ) {
        return handleExpiredSession(error, axiosInstance)
    }

    return Promise.reject(error)
}

export async function gatewayTimeoutInterceptor(error: ANY) {
    const errorMessage = error.response?.data?.errorMessage ||
        error.response?.data?.message ||
        error.response?.message ||
        'Unknown Error'

    const handleError = (title: string, detail: string = errorMessage) => {
        useNotification().error(title, {
            group: 'gateway-timeout-toast',
            detail,
        })
        // enhance crippled error object
        if (!error.response?.data?.errorMessage) {
            Object.assign(error, { response: { data: { errorMessage: detail } } })
        }
    }

    if (!error.response && !error.status && error.code !== AxiosError.ERR_CANCELED) {
        handleError('Response Error', 'No response from the server')
    }

    if (error.response?.status === 504) {
        handleError('Timeout Error')
    }

    return Promise.reject(error)
}
